import { notifications } from '@mantine/notifications';
import type { TypeApiError } from '@/types/api/types.api';

export type TypeFormLike = {
    setFieldError: (path: string, error: string) => void;
    getValues?: () => Record<string, unknown>;
};

const formHasPath = (values: Record<string, unknown> | undefined, path: string): boolean => {
    if (!values) return true;

    let cursor: any = values;
    for (const segment of path.split('.')) {
        if (cursor === null || typeof cursor !== 'object' || !(segment in cursor)) return false;
        cursor = cursor[segment];
    }
    return true;
};

let counter = 0;

const nextId = () => `bd-${Date.now().toString(36)}-${counter++}`;

export const notify = {
    loading(title: string, message?: string): string {
        const id = nextId();
        notifications.show({ id, loading: true, autoClose: false, withCloseButton: false, title, message: message ?? '' });
        return id;
    },

    update(id: string, options: { title: string; message?: string; color?: string }): void {
        notifications.update({ id, loading: false, autoClose: 4000, withCloseButton: true, color: options.color ?? 'success', title: options.title, message: options.message ?? '' });
    },

    progress(id: string, title: string, message: string): void {
        notifications.update({ id, loading: true, autoClose: false, withCloseButton: false, title, message });
    },

    success(title: string, message?: string): void {
        notifications.show({ color: 'success', title, message: message ?? '' });
    },

    error(title: string, message?: string): void {
        notifications.show({ color: 'danger', title, message: message ?? '' });
    },

    notice(color: 'danger' | 'warning' | 'info' | 'success', title: string, message?: string, onClick?: () => void): void {
        notifications.show({ color, title, message: message ?? '', autoClose: color === 'danger' ? 10_000 : 6_000, onClick, style: onClick ? { cursor: 'pointer' } : undefined });
    },

    hide(id: string): void {
        notifications.hide(id);
    },

    fromRejection(loadingId: string | null, error: unknown, form?: TypeFormLike): Record<string, string> | null {
        const payload = (error as { error?: TypeApiError })?.error ?? (error as TypeApiError);
        const raw = payload?.fields ?? null;
        const kept = raw ? Object.entries(raw).filter(([path]) => path !== 'code') : [];
        const fields = kept.length ? Object.fromEntries(kept) as Record<string, string> : null;

        if (loadingId) notifications.hide(loadingId);

        if (fields && form) {
            const values = form.getValues?.();
            const known = Object.entries(fields).filter(([path]) => formHasPath(values, path));
            const orphan = Object.entries(fields).filter(([path]) => !formHasPath(values, path));

            for (const [path, message] of known) form.setFieldError(path, message);

            if (orphan.length) {
                notifications.show({ color: 'danger', title: 'Azione non riuscita', message: orphan.map(([, message]) => message).join(' ') });
            } else if (!known.length) {
                notifications.show({ color: 'danger', title: 'Azione non riuscita', message: payload?.message ?? 'Riprova fra poco.' });
            }

            return fields;
        }

        const message = payload?.status === 0
            ? 'Il server non risponde: controlla la connessione e riprova.'
            : payload?.message ?? 'Riprova fra poco.';

        notifications.show({ color: 'danger', title: 'Azione non riuscita', message });
        return fields;
    },
};

export default notify;
